import { Divider } from 'antd';
import { useAppDispatch, useAppSelector } from 'app/hooks';
import { GalleryCardIcon, GalleryListIcon } from 'components/Icons';
import { RevxButton } from 'components/RevxButton';
import RevxFlexLayout from 'components/RevxFlexLayout';
import { GALLERY_RENDER_ENUM } from 'domains/gallerySkuDomain';
import { galleryDesignSizeKey } from 'utils/galleryDesignSize';
import { gallerySkuFilterKey } from 'utils/gallerySkuFilter';
import { useCanvaDesignSize, useCanvaMatchView, useGallerySkuFilter } from 'hooks';
import { useCallback, useEffect, useState } from 'react';
import './CompanyGallery.less';
import { CanvaMatchView, CompanyCard, CompanyGallerySlick } from './components';
import { CompanyList } from './components/CompanyList';

function CompanyGallery() {
  const dispatch = useAppDispatch();
  const galleryRenderType = useAppSelector(
    (state) => state.companyGallery.galleryRenderType
  );
  const filter = useGallerySkuFilter();
  const { designSize } = useCanvaDesignSize();
  const { isMatchView, browseAllProducts } = useCanvaMatchView();
  // See GalleryLayout: the count goes on the heading, and an empty match view is a dead end.
  const [matchCount, setMatchCount] = useState<number>();
  const handleMatchCount = useCallback(
    (total: number) => {
      setMatchCount(total);
      if (total === 0) {
        browseAllProducts({ replace: true });
      }
    },
    [browseAllProducts]
  );
  // Remount the lists when the filter changes so the infinite scroll starts over. The design size
  // is part of the key for the same reason: it reorders the whole result, so pages fetched before
  // it arrived were ordered by different rules and must not be appended to.
  const filterKey = `${gallerySkuFilterKey(filter)}|${galleryDesignSizeKey(
    designSize
  )}|${isMatchView ? 'match' : 'all'}`;
  useEffect(() => {
    dispatch.companyGallery.loadPageCompanyLabels(filter);
  }, [dispatch, filter]);
  const skuList = (
    <>
      {galleryRenderType === GALLERY_RENDER_ENUM.DEFAULT && (
        <CompanyCard
          key={`default-${filterKey}`}
          onTotalChange={isMatchView ? handleMatchCount : undefined}
        />
      )}
      {galleryRenderType === GALLERY_RENDER_ENUM.LIST && (
        <CompanyList key={`list-${filterKey}`} />
      )}
    </>
  );

  return (
    <div className="company-gallery">
      {!isMatchView && (
        <div className="company-gallery-slick-container">
          <CompanyGallerySlick />
        </div>
      )}
      <RevxFlexLayout
        justify="justify-end"
        className="company-gallery-slick-render"
      >
        <RevxButton
          type="link"
          onClick={() => {
            dispatch.companyGallery.setGalleryRenderType(
              galleryRenderType === GALLERY_RENDER_ENUM.DEFAULT
                ? GALLERY_RENDER_ENUM.LIST
                : GALLERY_RENDER_ENUM.DEFAULT
            );
          }}
        >
          {galleryRenderType === GALLERY_RENDER_ENUM.DEFAULT ? (
            <GalleryCardIcon />
          ) : (
            <GalleryListIcon />
          )}
        </RevxButton>
      </RevxFlexLayout>
      <Divider className="gallery-divider" />
      {isMatchView ? (
        <CanvaMatchView count={matchCount}>
          <div className="company-gallery-sku-container">{skuList}</div>
        </CanvaMatchView>
      ) : (
        <div className="company-gallery-sku-container">{skuList}</div>
      )}
    </div>
  );
}

export default CompanyGallery;
